import React, { useEffect, useMemo } from "react";
import { Box, CardContent, Grid } from "@mui/material";
import {
  BorderGreyBox,
  CardSpg,
  handleSubmitValidationFail,
  isNotEmpty,
  NameFilter,
  CodeFilter,
  IsActiveFilter,
  useReactHookForm,
} from "@duosoftbg/nacid-components";

import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReferenceDataList from "./ReferenceDataList";
import { useStore } from "react-redux";
import { SEARCH_FILTERS_GROUP } from "../../../../config/search/filters/groupsConfig";
import { ReferenceDataFilterDetails } from "../../../../types/referenceDataTypes";
import { referenceDataFilterInitialValues } from "../../../../init/referenceDataFilterInitialValues";
import { createReferenceDataFilterValidationSchema } from "../../../../yup/schema/referenceData/referenceDataFilterValidationSchema";
import useSearchTableControl from "../../../../hooks/portal/search/useSearchTableControl";
import SearchFilters from "../../common/search/filters/SearchFilters";
import ReferenceDataDomainFilter from "../../common/search/filters/definition/autocomplete/ReferenceDataDomainFilter";
import IndexFilter from "../../common/search/filters/definition/IndexFilter";
import { searchReferenceData } from "../../../../axios/api/services";
import { RootState } from "../../../../store/redux/store";

const ReferenceDataFilters = () => {
  return (
    <Grid container spacing={5} px={2} py={6}>
      <ReferenceDataDomainFilter />
      <CodeFilter />
      <NameFilter />
      <IndexFilter />
      <IsActiveFilter />
    </Grid>
  );
};

const ReferenceData = () => {
  const { t } = useTranslation();
  const store = useStore();
  const group = SEARCH_FILTERS_GROUP.REFERENCE_DATA;

  const { methods } = useReactHookForm<ReferenceDataFilterDetails>({
    defaultValues: referenceDataFilterInitialValues,
    validationSchema: createReferenceDataFilterValidationSchema,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.portalSearchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: referenceDataFilterInitialValues,
    filterData: searchReferenceData,
    callAfterResetFilters: true,
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t),
        )}
      >
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <Box>
              <BorderGreyBox>
                <SearchFilters
                  isSubmitBtnDisabled={control.isSubmitBtnDisabled}
                  isResetBtnDisabled={control.isResetBtnDisabled}
                  handleResetFilters={control.handleResetFilters}
                >
                  {useMemo(
                    () => (
                      <ReferenceDataFilters />
                    ),
                    [],
                  )}
                </SearchFilters>
              </BorderGreyBox>
              {control.showList && (
                <ReferenceDataList
                  records={control.records}
                  total={control.total}
                  isLoading={control.isLoading}
                  onPageOrOrderChange={control.handlePageOrOrderChange}
                  blockTable={control.blockTable}
                  group={group}
                  filterFn={control.fetch}
                />
              )}
            </Box>
          </CardContent>
        </CardSpg>
      </form>
    </FormProvider>
  );
};

export default ReferenceData;
